import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Shield, RotateCw, PlayCircle, AlertTriangle, CheckCircle2, Clock, Loader2 } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'

// PCI DSS SAQ A v4.0 Requirement 11.6.1 — payment page tamper detection.
// Super-admin only surface for reviewing baseline status + alerts and
// rebuilding baselines after legitimate deploys.

interface BaselineRow {
  url: string
  has_baseline: boolean
  captured_at: string | null
  captured_by_name: string | null
  captured_by_email: string | null
  note: string | null
  open_alert_count: number
}

interface AlertRow {
  id: string
  url: string
  alert_type: 'headers_changed' | 'body_changed' | 'scripts_changed' | 'fetch_failed'
  diff_summary: string
  created_at: string
  acknowledged_at: string | null
  acknowledged_by_name: string | null
  acknowledged_by_email: string | null
  acknowledgment_reason: string | null
}

const ALERT_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  headers_changed: { label: 'Headers changed', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
  scripts_changed: { label: 'Scripts changed', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
  body_changed:    { label: 'Body changed',    color: 'text-amber-300 bg-amber-500/10 border-amber-500/20' },
  fetch_failed:    { label: 'Fetch failed',    color: 'text-orange-300 bg-orange-500/10 border-orange-500/20' },
}

export default function PciTamperDetection() {
  const qc = useQueryClient()
  const [alertFilter, setAlertFilter] = useState<'open' | 'all'>('open')

  const baselines = useQuery<{ baselines: BaselineRow[] }>({
    queryKey: ['pci-baselines'],
    queryFn: () => api.get('/pci/tamper/baselines').then(r => r.data),
  })

  const alerts = useQuery<{ alerts: AlertRow[] }>({
    queryKey: ['pci-alerts', alertFilter],
    queryFn: () => api.get(`/pci/tamper/alerts?status=${alertFilter}&limit=50`).then(r => r.data),
  })

  const runNow = useMutation({
    mutationFn: () => api.post('/pci/tamper/run-now').then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pci-baselines'] })
      qc.invalidateQueries({ queryKey: ['pci-alerts'] })
    },
  })

  const rebuild = useMutation({
    mutationFn: (v: { url: string; note: string }) =>
      api.post('/pci/tamper/baselines/rebuild', v).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pci-baselines'] })
      qc.invalidateQueries({ queryKey: ['pci-alerts'] })
    },
  })

  const acknowledge = useMutation({
    mutationFn: (v: { id: string; reason: string }) =>
      api.post(`/pci/tamper/alerts/${v.id}/acknowledge`, { reason: v.reason }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pci-alerts'] })
      qc.invalidateQueries({ queryKey: ['pci-baselines'] })
    },
  })

  const [expandedAlert, setExpandedAlert] = useState<string | null>(null)
  const [ackReasonById, setAckReasonById] = useState<Record<string, string>>({})
  const [rebuildNoteByUrl, setRebuildNoteByUrl] = useState<Record<string, string>>({})

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield size={24} className="text-[#C41E3A]" />
          <div>
            <h1 className="text-2xl font-bold text-white">Payment page tamper detection</h1>
            <p className="text-xs text-gray-500 mt-0.5">PCI DSS 11.6.1 · nightly cron + on-demand check</p>
          </div>
        </div>
        <button
          onClick={() => runNow.mutate()}
          disabled={runNow.isPending}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-sm text-gray-300 hover:bg-white/5 disabled:opacity-50"
        >
          {runNow.isPending ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
          Run check now
        </button>
      </div>

      {runNow.data && (
        <div className="mb-5 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
          Ran check: {runNow.data.checked} URL(s) checked, {runNow.data.alerted} alert(s), {runNow.data.skipped_no_baseline} skipped (no baseline).
        </div>
      )}

      {/* Baselines section */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">Monitored URLs</h2>
        <div className="space-y-3">
          {(baselines.data?.baselines ?? []).map((b) => (
            <div key={b.url} className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <code className="text-sm text-white break-all">{b.url}</code>
                    {b.open_alert_count > 0 ? (
                      <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/20">
                        <AlertTriangle size={11} /> {b.open_alert_count} open
                      </span>
                    ) : b.has_baseline ? (
                      <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                        <CheckCircle2 size={11} /> healthy
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/20">
                        <Clock size={11} /> no baseline
                      </span>
                    )}
                  </div>
                  {b.has_baseline ? (
                    <p className="text-xs text-gray-500">
                      Baseline captured {formatDateTime(b.captured_at!)}
                      {b.captured_by_name && <> by <span className="text-gray-400">{b.captured_by_name}</span></>}
                      {b.note && <> — <span className="text-gray-400 italic">{b.note}</span></>}
                    </p>
                  ) : (
                    <p className="text-xs text-amber-300/80">
                      No baseline captured yet — the cron will skip this URL until you rebuild.
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Note (e.g. after v1.5 deploy)"
                  value={rebuildNoteByUrl[b.url] ?? ''}
                  onChange={e => setRebuildNoteByUrl(s => ({ ...s, [b.url]: e.target.value }))}
                  className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-[#C41E3A]"
                />
                <button
                  onClick={() => rebuild.mutate({ url: b.url, note: rebuildNoteByUrl[b.url] ?? '' })}
                  disabled={rebuild.isPending && rebuild.variables?.url === b.url}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-white/10 text-xs text-gray-300 hover:bg-white/5 disabled:opacity-50"
                >
                  {rebuild.isPending && rebuild.variables?.url === b.url ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <RotateCw size={12} />
                  )}
                  Rebuild baseline
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Alerts section */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Alerts</h2>
          <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
            <button
              onClick={() => setAlertFilter('open')}
              className={`px-3 py-1.5 text-xs ${alertFilter === 'open' ? 'bg-[#C41E3A] text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
            >
              Open
            </button>
            <button
              onClick={() => setAlertFilter('all')}
              className={`px-3 py-1.5 text-xs ${alertFilter === 'all' ? 'bg-[#C41E3A] text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'}`}
            >
              All
            </button>
          </div>
        </div>

        {alerts.data && alerts.data.alerts.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 text-center text-sm text-gray-500">
            {alertFilter === 'open' ? 'No open alerts — everything matches its baseline.' : 'No alerts yet.'}
          </div>
        ) : (
          <div className="space-y-2">
            {(alerts.data?.alerts ?? []).map((a) => {
              const labelInfo = ALERT_TYPE_LABELS[a.alert_type] ?? { label: a.alert_type, color: 'text-gray-400 bg-white/5 border-white/10' }
              const isExpanded = expandedAlert === a.id
              return (
                <div key={a.id} className={`border rounded-xl px-4 py-3 ${a.acknowledged_at ? 'bg-white/[0.02] border-white/5' : 'bg-white/5 border-white/10'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-[11px] px-1.5 py-0.5 rounded border ${labelInfo.color}`}>
                          {labelInfo.label}
                        </span>
                        <code className="text-xs text-gray-400 truncate">{a.url}</code>
                        <span className="text-[11px] text-gray-500">{formatDateTime(a.created_at)}</span>
                        {a.acknowledged_at && (
                          <span className="text-[11px] text-emerald-400/80">
                            ack {formatDateTime(a.acknowledged_at)}{a.acknowledged_by_name && ` by ${a.acknowledged_by_name}`}
                          </span>
                        )}
                      </div>
                      {a.acknowledgment_reason && (
                        <p className="text-xs text-emerald-300/80 mb-1">Reason: {a.acknowledgment_reason}</p>
                      )}
                      <button
                        onClick={() => setExpandedAlert(isExpanded ? null : a.id)}
                        className="text-xs text-gray-400 hover:text-white"
                      >
                        {isExpanded ? 'Hide diff ↑' : 'Show diff ↓'}
                      </button>
                      {isExpanded && (
                        <pre className="mt-2 text-[11px] text-gray-400 bg-black/30 border border-white/5 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">{a.diff_summary}</pre>
                      )}
                    </div>
                  </div>
                  {!a.acknowledged_at && (
                    <div className="mt-3 flex items-center gap-2">
                      <input
                        type="text"
                        placeholder="Reason (required — e.g. legitimate deploy, false positive, investigated)"
                        value={ackReasonById[a.id] ?? ''}
                        onChange={e => setAckReasonById(s => ({ ...s, [a.id]: e.target.value }))}
                        className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-[#C41E3A]"
                      />
                      <button
                        onClick={() => acknowledge.mutate({ id: a.id, reason: ackReasonById[a.id] ?? '' })}
                        disabled={acknowledge.isPending && acknowledge.variables?.id === a.id || !(ackReasonById[a.id] ?? '').trim()}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-white/10 text-xs text-gray-300 hover:bg-white/5 disabled:opacity-50"
                      >
                        {acknowledge.isPending && acknowledge.variables?.id === a.id ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <CheckCircle2 size={12} />
                        )}
                        Acknowledge
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </section>

      <p className="text-[11px] text-gray-500 mt-6 leading-relaxed max-w-3xl">
        Runs nightly at 00:00 UTC as part of the daily cron block. Every deploy that changes any payment surface will fire an alert here — that&apos;s the point of tamper detection.
        Review the diff, confirm it matches an intentional change, add a reason, then acknowledge or rebuild the baseline. Rebuilds also auto-acknowledge any open alerts on that URL.
      </p>
    </div>
  )
}
